import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTestimonials } from '@/lib/testimonialsSource';
import TestimonialCarousel from '@/components/sections/TestimonialCarousel';
import { cn } from '@/lib/utils';
import { Shield, TrendingUp } from 'lucide-react';

const TestimonialCard = ({ data }) => {
  const Icon = data.iconType === 'shield' ? Shield : TrendingUp;

  // No Review JSON-LD: testimonials are attributed by role, not by a verifiable person,
  // and self-serving reviews on the Organization are ignored/penalised by Google.

  return (
    <motion.div
      initial="initial"
      whileHover="hover"
      className="relative h-full"
    >

      <div className="h-full bg-white/[0.03] backdrop-blur-md border border-white/[0.08] rounded-[2px] p-8 md:p-12 relative overflow-hidden group transition-all duration-300 hover:border-[#4A90E2]/45 hover:bg-white/[0.06]">
        
        {/* Top accent stripe — slides in on hover */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#4A90E2] opacity-0 group-hover:opacity-70 transition-opacity duration-300" />

        {/* Corner glow blob */}
        <div className="absolute bottom-[-32px] right-[-32px] w-40 h-40 rounded-full blur-3xl opacity-[0.06] bg-[#4A90E2] group-hover:opacity-[0.14] transition-opacity duration-400 pointer-events-none" />

        <div className="relative z-10 flex flex-col h-full justify-between">
          <blockquote className="font-body italic text-[20px] leading-relaxed text-[#F5F3F0] mb-8">
            &ldquo;{data.quote}&rdquo;
          </blockquote>

          <div className="border-t border-white/10 pt-6 flex justify-between items-end">
             <div>
               <cite className="font-heading font-bold text-sm text-gray-400 not-italic uppercase tracking-wider block" data-testid="testimonial-author">
                 — {data.author}
               </cite>
               {data.relatedCaseId && (
                 <Link
                   to={`/intelligence/${data.relatedCaseId}`}
                   className="font-mono text-[10px] uppercase tracking-widest text-[#4A90E2] hover:text-white transition-colors mt-2 inline-block"
                   data-testid="testimonial-case-link"
                 >
                   Read the case study →
                 </Link>
               )}
             </div>
          </div>
        </div>

        {/* Hover Impact: Proof Stat Slide-out */}
        <motion.div
          variants={{
            initial: { x: "100%" },
            hover: { x: "0%" }
          }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="absolute top-0 right-0 bottom-0 w-1/3 bg-[#0f1218]/95 backdrop-blur-md border-l border-[#4A90E2]/60 flex items-center justify-center z-20 shadow-[-10px_0_20px_rgba(0,0,0,0.6)]"
        >
           <span className="font-mono text-[#4A90E2] text-xl md:text-2xl font-bold rotate-90 whitespace-nowrap tracking-widest">
             {data.proofStat}
           </span>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default function SocialProof() {
  // Live from the shared Google Sheet (tab "Testimonials"); bundled copy paints first.
  const { testimonials, source } = useTestimonials();
  return (
    <section className="py-24 bg-[#1F232B] border-b-2 border-border" data-testid="social-proof" data-source={source}>
      <div className="container mx-auto px-4">
        <div className="mb-16">
          <h2 className="font-title text-3xl md:text-[42px] text-white uppercase tracking-wide leading-none mb-4">
            Results, Not Rhetoric.
          </h2>
          <div className="h-[2px] w-24 bg-[#4A90E2]"></div>
        </div>

        {testimonials.length > 2 ? (
          // 3+ testimonials → carousel (two per view on desktop, one on mobile)
          <TestimonialCarousel items={testimonials} renderItem={(t) => <TestimonialCard data={t} />} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="testimonial-grid">
            {testimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.id} data={testimonial} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
